import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';

import Block from '@mui/icons-material/Block';
import Check from '@mui/icons-material/Check';

import Button from '@shared/atoms/Button';
import Form from '@shared/atoms/Form';
import Icon from '@shared/atoms/Icon';
import Tooltip from '@shared/atoms/Tooltip';
import T from '@shared/atoms/Typography';
import Accordion from '@shared/molecules/Accordion';
import Dialog from '@shared/molecules/Dialog';
import theme from '@shared/themes/default';
import { formatDate, useForm, newSelectTranslation } from '@shared/utils';

import { stripeSubscriptionStatus, ORGANIZATION_TYPE } from '@shared/const';

import {
  updateOrganization,
  updateSubscription,
} from '../../graphql/mutations';
import { getMinAllowedDate } from '../DatePicker';
import InvoiceForm from '../InvoiceForm';
import { useAppGlobals } from '../../../../contexts/AppContext';

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
`;

const SubscriptionWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${theme.spacing.m3};
`;

const SubscriptionDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 300px;
`;

const AccordionWrapper = styled.div`
  padding: ${theme.spacing.m1};
`;

const AccordionContentWrapper = styled.div`
  padding: ${theme.spacing.m1};
`;

const FormWrapper = styled.div`
  align-items: end;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
  max-width: 600px;
`;

const SubscriptionTab = ({
  item,
  onUpdateOrganization,
  setErrorMessage,
  setHasError,
  setHasSuccess,
  setSuccessMessage,
}) => {
  const { t } = useTranslation();
  const { locale, useGraphQLMutation } = useAppGlobals();

  const [expanded, setExpanded] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [subscriptionDialogData, setSubscriptionDialogData] = useState({});
  const [subscriptionStopNow, setSubscriptionStopNow] = useState(false);

  const isStarted = (subscription) => {
    return subscription.status === stripeSubscriptionStatus.ACTIVE;
  };

  const [updateOrganizationMutation, updateOrganizationMutationState] =
    useGraphQLMutation(updateOrganization, {
      update(cache, { data: { updateOrganization } }) {
        onUpdateOrganization(cache, updateOrganization);
        setSuccessMessage(
          t('backoffice.organizations.subscription.successfullyUpdatedInvoice')
        );
        setHasSuccess(true);
      },
      onError() {
        setErrorMessage(
          t('backoffice.organizations.subscription.failedToUpdateInvoice')
        );
        setHasError(true);
      },
    });

  const [updateSubscriptionMutation, updateSubscriptionMutationState] =
    useGraphQLMutation(updateSubscription, {
      update(cache, { data: { updateSubscription } }) {
        cache.modify({
          id: cache.identify(item),
          fields: {
            subscription() {
              return cache.writeFragment({
                id: cache.identify(updateSubscription),
                fragment: gql`
                  fragment Sub on Subscription {
                    id
                    expirationDate
                    product {
                      id
                      name {
                        languageCode
                        value
                      }
                      catalogs {
                        id
                      }
                    }
                    status
                    validFrom
                    validTo
                    seats
                    invoice
                  }
                `,
                data: updateSubscription,
              });
            },
          },
        });
      },
      onCompleted() {
        setSuccessMessage(
          isStarted(subscriptionDialogData)
            ? t(
                'backoffice.organizations.subscription.successfullyStoppedSubscription'
              )
            : t(
                'backoffice.organizations.subscription.successfullyStartedSubscription'
              )
        );
        setHasSuccess(true);
        setSubscriptionDialogOpen(false);
      },
      onError() {
        setErrorMessage(
          isStarted(subscriptionDialogData)
            ? t(
                'backoffice.organizations.subscription.failedToStopSubscription'
              )
            : t(
                'backoffice.organizations.subscription.failedToStartSubscription'
              )
        );
        setHasError(true);
        setSubscriptionDialogOpen(false);
      },
    });

  const {
    disableSubmit,
    errors,
    handleSubmit,
    handleBlur,
    handleChange,
    properties,
    setDefaults,
    submitting,
    values,
  } = useForm({
    changesRequired: true,
    fields: {
      organizationNumber: {
        maxLength: 9,
      },
      invoiceSendVia: {},
      invoiceName: {},
      invoiceEmail: {
        lowerCase: true,
        type: 'email',
      },
      invoiceAddress: {},
      invoiceZip: {
        maxLength: 4,
      },
      invoiceCity: {},
      invoicePeriod: {},
      invoiceNote: {},
      invoiceReference: {},
      purchasedLicenses: {
        mode: 'numeric',
        pattern: '[0-9]*',
        validation: (value) => /^[0-9]*$/.test(value),
      },
      startDate: {},
      stopDate: {},
    },
    onSubmitHook: async (organization) => {
      const input = Object.entries(organization).reduce(
        (acc, [key, value]) => {
          if (value) {
            acc[key] = value;
          } else {
            acc[key] = null;
          }
          return acc;
        },
        {
          id: item.id,
        }
      );

      if (input.invoicePeriod === 'none') {
        input.invoicePeriod = null;
      }
      if (input.invoiceSendVia === 'none') {
        input.invoiceSendVia = null;
      }
      if (input.purchasedLicenses) {
        input.purchasedLicenses = parseInt(input.purchasedLicenses, 10);
      }

      if (
        item.subscription &&
        item.subscription.status !== stripeSubscriptionStatus.NOT_STARTED
      ) {
        delete input.startDate;
      }

      if (item.subscription && input.stopDate !== undefined) {
        const originalStopDate = item.subscription.expirationDate || '';
        let newStopDate = input.stopDate || '';

        if (newStopDate) {
          const minAllowedDate = getMinAllowedDate();
          const stopDateObj = new Date(newStopDate);
          if (stopDateObj < minAllowedDate) {
            newStopDate = minAllowedDate.toISOString();
          }
        }

        if (originalStopDate !== newStopDate) {
          await updateSubscriptionMutation({
            variables: {
              input: {
                id: item.subscription.id,
                expirationDate: newStopDate || null,
              },
            },
          });
        }
        delete input.stopDate;
      }

      if (!item.subscription) {
        delete input.stopDate;
      }

      updateOrganizationMutation({
        variables: {
          input,
        },
      });
    },
    onSubmitHookEnded: !updateOrganizationMutationState.loading,
  });

  useEffect(() => {
    setDefaults({
      organizationNumber: item.organizationNumber || '',
      invoiceSendVia: item.invoiceSendVia || 'none',
      invoiceName: item.invoiceName || '',
      invoiceEmail: item.invoiceEmail || '',
      invoiceAddress: item.invoiceAddress || '',
      invoiceZip: item.invoiceZip || '',
      invoiceCity: item.invoiceCity || '',
      invoicePeriod: item.invoicePeriod || 'none',
      invoiceNote: item.invoiceNote || '',
      invoiceReference: item.invoiceReference || '',
      purchasedLicenses: `${item.subscription?.seats || ''}`,
      startDate: item.subscription?.validFrom || '',
      stopDate: item.subscription?.expirationDate || '',
    });
  }, [item]);

  useEffect(() => {
    if (!subscriptionDialogOpen) {
      setSubscriptionDialogData({});
    }
  }, [subscriptionDialogOpen]);

  const getSubscriptionStatus = (subscription) => {
    if (subscription.status === stripeSubscriptionStatus.PAUSED) {
      return t('backoffice.organizations.subscription.expires', {
        date: formatDate({
          date: subscription.expirationDate,
        }),
      });
    }
    if (subscription.status === stripeSubscriptionStatus.ACTIVE) {
      return t('backoffice.organizations.subscription.active');
    }
    if (subscription.status === stripeSubscriptionStatus.NOT_STARTED) {
      return t('backoffice.organizations.subscription.startsOn', {
        date: formatDate({
          date: subscription.validFrom,
        }),
      });
    }
    return t('backoffice.organizations.subscription.stopped');
  };

  const getSubscriptionDialogText = (subscriptionDialogData, item) => {
    const productName = newSelectTranslation(
      subscriptionDialogData.product?.name,
      locale
    );

    if (item.subscription?.invoice) {
      return isStarted(subscriptionDialogData)
        ? t('backoffice.organizations.subscription.wantToStop', {
            name: productName,
          })
        : t('backoffice.organizations.subscription.wantToStart', {
            name: productName,
          });
    }

    return isStarted(subscriptionDialogData)
      ? t('backoffice.organizations.subscription.wantToStopNotInvoice', {
          name: productName,
        })
      : t('backoffice.organizations.subscription.wantToStart', {
          name: productName,
        });
  };

  return (
    <>
      <OuterWrapper>
        {item.subscription ? (
          <SubscriptionWrapper key={item.subscription.id}>
            {item.subscription.status === stripeSubscriptionStatus.ACTIVE ? (
              <Icon color={theme.palette.successLight} icon={Check} />
            ) : (
              <Icon color={theme.palette.errorLight} icon={Block} />
            )}
            <SubscriptionDescriptionWrapper>
              <T>
                {item.type === ORGANIZATION_TYPE.ENTERPRISE
                  ? ORGANIZATION_TYPE.ENTERPRISE
                  : newSelectTranslation(
                      item.subscription.product.name,
                      locale
                    )}
              </T>
              <T color={theme.palette.lightInteracting} variant="paragraphS">
                {getSubscriptionStatus(item.subscription)}
              </T>
            </SubscriptionDescriptionWrapper>
            {isStarted(item.subscription) && item.subscription?.invoice && (
              <Tooltip
                arrow
                title={t(
                  'backoffice.organizations.subscription.stopNowTooltip'
                )}
              >
                <div>
                  <Button
                    ariaLabel={t(
                      'backoffice.organizations.subscription.stopNow'
                    )}
                    disabled={
                      item.subscription.status ===
                      stripeSubscriptionStatus.NOT_STARTED
                    }
                    onClick={() => {
                      setSubscriptionDialogOpen(true);
                      setSubscriptionDialogData(item.subscription);
                      setSubscriptionStopNow(true);
                    }}
                    theme="dark"
                    type={
                      isStarted(item.subscription) ? 'secondary' : 'primary'
                    }
                  >
                    {t('backoffice.organizations.subscription.stopNow')}
                  </Button>
                </div>
              </Tooltip>
            )}
            {item.subscription?.invoice && !isStarted(item.subscription) && (
              <Tooltip
                arrow
                title={t('backoffice.organizations.subscription.startTooltip')}
              >
                <div>
                  <Button
                    ariaLabel={t('backoffice.organizations.subscription.start')}
                    disabled={
                      item.subscription.status ===
                      stripeSubscriptionStatus.NOT_STARTED
                    }
                    onClick={() => {
                      setSubscriptionDialogOpen(true);
                      setSubscriptionDialogData(item.subscription);
                      setSubscriptionStopNow(false);
                    }}
                    theme="dark"
                    type="primary"
                  >
                    {t('backoffice.organizations.subscription.start')}
                  </Button>
                </div>
              </Tooltip>
            )}
            {!item.subscription.invoice && (
              <Tooltip
                arrow
                title={t('backoffice.organizations.subscription.startTooltip')}
              >
                <div>
                  <Button
                    ariaLabel={
                      isStarted(item.subscription)
                        ? t('backoffice.organizations.subscription.stop')
                        : t('backoffice.organizations.subscription.start')
                    }
                    disabled={
                      item.subscription.status ===
                      stripeSubscriptionStatus.NOT_STARTED
                    }
                    onClick={() => {
                      setSubscriptionDialogOpen(true);
                      setSubscriptionDialogData(item.subscription);
                      setSubscriptionStopNow(false);
                    }}
                    theme="dark"
                    type={
                      isStarted(item.subscription) ? 'secondary' : 'primary'
                    }
                  >
                    {isStarted(item.subscription)
                      ? t('backoffice.organizations.subscription.stop')
                      : t('backoffice.organizations.subscription.start')}
                  </Button>
                </div>
              </Tooltip>
            )}
          </SubscriptionWrapper>
        ) : (
          <>
            <T align="center" fontWeight="bold" variant="headingXs">
              {t('backoffice.organizations.subscription.noSubscriptionFound')}
            </T>
            <T align="center" variant="paragraph">
              {item.type === ORGANIZATION_TYPE.ENTERPRISE
                ? t('backoffice.organizations.subscription.createSubscription')
                : t('backoffice.organizations.subscription.selfServeTeam')}
            </T>
          </>
        )}
        <Accordion
          summary={
            <AccordionWrapper>
              <T fontWeight="medium" variant="subtitle">
                {t('backoffice.organizations.subscription.invoice')}
              </T>
            </AccordionWrapper>
          }
          details={
            <AccordionContentWrapper>
              <Form onSubmit={handleSubmit}>
                <FormWrapper>
                  <InvoiceForm
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    properties={properties}
                    theme="dark"
                    values={values}
                    showSubscriptionData={
                      item.subscription
                        ? item.subscription.invoice
                        : item.type === ORGANIZATION_TYPE.ENTERPRISE
                    }
                    disableStartDate={
                      item.subscription
                        ? item.subscription.status ===
                            stripeSubscriptionStatus.ACTIVE ||
                          item.subscription.status ===
                            stripeSubscriptionStatus.PAUSED
                        : false
                    }
                    disableStopDate={
                      !item.subscription ||
                      (item.subscription.expirationDate &&
                        new Date() >=
                          new Date(item.subscription.expirationDate))
                    }
                    disablePurchasedLicenses={
                      item.type === ORGANIZATION_TYPE.TEAM
                    }
                  />
                  <Button
                    ariaLabel={t('generic.save')}
                    disabled={disableSubmit}
                    loading={submitting}
                    loadingOnIcon={submitting}
                    submit
                    theme="dark"
                  >
                    {t('generic.save')}
                  </Button>
                </FormWrapper>
              </Form>
            </AccordionContentWrapper>
          }
          setExpanded={setExpanded}
          expanded={expanded}
          theme="dark"
        />
      </OuterWrapper>
      <Dialog
        ariaLabel={
          isStarted(subscriptionDialogData)
            ? t('backoffice.organizations.subscription.stopSubscription')
            : t('backoffice.organizations.subscription.startSubscription')
        }
        actions={[
          {
            label: t('generic.cancel'),
            handler: () => {
              setSubscriptionDialogOpen(false);
              setSubscriptionDialogData({});
            },
            type: 'secondaryOutline',
          },
          {
            label: t('generic.confirm'),
            handler: () =>
              updateSubscriptionMutation({
                variables: {
                  input: {
                    id: subscriptionDialogData.id,
                    action: isStarted(subscriptionDialogData)
                      ? 'cancel'
                      : 'resume',
                    stopNow: subscriptionStopNow,
                  },
                },
              }),
          },
        ]}
        loading={updateSubscriptionMutationState.loading}
        onClose={() => {
          setSubscriptionDialogOpen(false);
        }}
        open={subscriptionDialogOpen}
        title={
          isStarted(subscriptionDialogData)
            ? t('backoffice.organizations.subscription.stopSubscription')
            : t('backoffice.organizations.subscription.startSubscription')
        }
      >
        <T color={theme.palette.mainDark}>
          {getSubscriptionDialogText(subscriptionDialogData, item, locale, t)}
        </T>
      </Dialog>
    </>
  );
};

SubscriptionTab.propTypes = {
  item: PropTypes.object.isRequired,
  onUpdateOrganization: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setHasError: PropTypes.func.isRequired,
  setHasSuccess: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};

export default SubscriptionTab;
